/**
 * Checks shown on site, before any drawing exists: what is missing in the survey itself, then
 * the project's rule pack (RGIE, NF C 15-100, NEN 1010) run on the diagrams the survey would
 * generate (a preview, never applied).
 */
import {
  applyTransaction,
  boardCircuits,
  circuitPoints,
  roomPoints,
  surveyBoards,
  surveyRooms,
  type Id,
  type Project,
  tr,
} from '@elec/domain';
import { checkProject, earthElectrode, standardOf, type Severity } from '@elec/rules';
import { ROOM_TYPES } from '@elec/symbols';
import { generateFromSurvey, LABELS, surveyCodes, type GenerateLabels } from './generate.ts';

type L = { fr: string; en: string; nl: string };

export interface SurveyFinding {
  id: string;
  severity: Severity;
  message: L;
  /** What to open when the finding is tapped. */
  target?: { kind: 'room' | 'board' | 'circuit'; id: Id };
  /** Found by the project's rule pack on the generated diagrams. */
  fromDiagram?: boolean;
}

/** Diagram rules that repeat a survey check, or concern drawing details the survey does not set. */
const COVERED = new Set([
  'R-CIR-04',
  'R-CIR-06',
  'R-CIR-07',
  'R-DOC-01',
  'R-DOC-04',
  'R-TOPO-01',
  'R-ML-01',
  'R-SRV-01',
]);

const WET = new Set(ROOM_TYPES.filter((r) => r.wet).map((r) => r.value));

/**
 * The project with the diagrams the survey gives (generated in memory, nothing dispatched), or
 * null when the survey has no circuits yet or cannot be generated (concurrent edits). Circuit
 * letters and RCD tags are the ones generation will use, so measurements can be recorded on site
 * before anything is generated.
 */
export function surveyPreview(
  project: Project,
  labels: GenerateLabels = LABELS[project.meta.locale],
): Project | null {
  if (!surveyBoards(project).some((b) => boardCircuits(project, b.id).length)) return null;
  try {
    const { ops } = generateFromSurvey(project, labels);
    return applyTransaction(project, { label: '', ops }).project;
  } catch {
    return null;
  }
}

export function surveyFindings(
  project: Project,
  labels: GenerateLabels = LABELS[project.meta.locale],
): SurveyFinding[] {
  const out: SurveyFinding[] = [];
  const codes = surveyCodes(project);
  const cname = (id: Id) => {
    const c = project.surveyCircuits[id];
    const code = codes.get(id) ?? '?';
    return c?.label ? `${code} (${c.label})` : code;
  };
  const rooms = surveyRooms(project);
  const standard = standardOf(project);
  const boards = surveyBoards(project);

  if (!rooms.length)
    out.push({
      id: 'S-ROOM-00',
      severity: 'info',
      message: { fr: 'Aucune pièce relevée', en: 'No rooms recorded', nl: 'Geen ruimtes opgenomen' },
    });
  if (!boards.length)
    out.push({
      id: 'S-BOARD-00',
      severity: 'warning',
      message: { fr: 'Aucun tableau relevé', en: 'No board recorded', nl: 'Geen bord opgenomen' },
    });

  for (const r of rooms) {
    const pts = roomPoints(project, r.id);
    const name = r.name || '—';
    if (!pts.length)
      out.push({
        id: 'S-ROOM-01',
        severity: 'info',
        message: { fr: `${name} : aucun point`, en: `${name}: no points`, nl: `${name}: geen punten` },
        target: { kind: 'room', id: r.id },
      });
    const free = pts.filter((p) => !p.circuitId || !project.surveyCircuits[p.circuitId]);
    if (free.length)
      out.push({
        id: 'S-PT-01',
        severity: 'warning',
        message: {
          fr: `${name} : ${free.reduce((n, p) => n + p.quantity, 0)} point(s) sans circuit`,
          en: `${name}: ${free.reduce((n, p) => n + p.quantity, 0)} point(s) without a circuit`,
          nl: `${name}: ${free.reduce((n, p) => n + p.quantity, 0)} punt(en) zonder kring`,
        },
        target: { kind: 'room', id: r.id },
      });
    if (WET.has(r.roomType) && !r.name)
      out.push({
        id: 'S-ROOM-02',
        severity: 'info',
        message: {
          fr: 'Pièce humide sans nom',
          en: 'Wet room without a name',
          nl: 'Vochtige ruimte zonder naam',
        },
        target: { kind: 'room', id: r.id },
      });
  }

  for (const b of boards) {
    const bname = b.name || tr(project.meta.locale, 'Tableau', 'Board', 'Bord');
    const cs = boardCircuits(project, b.id);
    if (!cs.length)
      out.push({
        id: 'S-BOARD-01',
        severity: 'warning',
        message: {
          fr: `${bname} : aucun circuit`,
          en: `${bname}: no circuits`,
          nl: `${bname}: geen kringen`,
        },
        target: { kind: 'board', id: b.id },
      });
    // What the earth electrode must meet depends on the standard (RGIE: always, below 30 Ω;
    // NF C 15-100 and NEN 1010: in TT, RA × IΔn ≤ 50 V).
    const sens = [b.mainRcd, ...b.rcds]
      .map((r) => Number(r?.sensitivityMa))
      .filter((v) => Number.isFinite(v) && v > 0);
    const earth = earthElectrode(standard, project.meta.earthingSystem, sens);
    if (earth.required && b.earthOhm === undefined && b === boards[0])
      out.push({
        id: 'S-EARTH-01',
        severity: 'warning',
        message: {
          fr: 'Résistance de la prise de terre non mesurée',
          en: 'Earth electrode resistance not measured',
          nl:
            standard === 'nen1010'
              ? 'Aardverspreidingsweerstand niet gemeten'
              : 'Spreidingsweerstand van de aardverbinding niet gemeten',
        },
        target: { kind: 'board', id: b.id },
      });
    else if (earth.required && b.earthOhm !== undefined && b.earthOhm > earth.maxOhm)
      out.push({
        id: 'S-EARTH-02',
        severity: 'error',
        message: {
          fr: `Prise de terre ${b.earthOhm} Ω (max. ${earth.maxOhm} Ω)`,
          en: `Earth electrode ${b.earthOhm} Ω (max. ${earth.maxOhm} Ω)`,
          nl:
            standard === 'nen1010'
              ? `Aardelektrode ${b.earthOhm} Ω (max. ${earth.maxOhm} Ω)`
              : `Aardverbinding ${b.earthOhm} Ω (max. ${earth.maxOhm} Ω)`,
        },
        target: { kind: 'board', id: b.id },
      });
    for (const c of cs) {
      const target = { kind: 'circuit' as const, id: c.id };
      if (!circuitPoints(project, c.id).length)
        out.push({
          id: 'S-CIR-01',
          severity: 'info',
          message: {
            fr: `Circuit ${cname(c.id)} : aucun point`,
            en: `Circuit ${cname(c.id)}: no points`,
            nl: `Kring ${cname(c.id)}: geen punten`,
          },
          target,
        });
      if (!c.ratingA)
        out.push({
          id: 'S-CIR-02',
          severity: 'warning',
          message: {
            fr: `Circuit ${cname(c.id)} : calibre manquant`,
            en: `Circuit ${cname(c.id)}: rating missing`,
            nl: `Kring ${cname(c.id)}: nominale stroom ontbreekt`,
          },
          target,
        });
      if (!c.cable?.sectionMm2)
        out.push({
          id: 'S-CIR-03',
          severity: 'warning',
          message: {
            fr: `Circuit ${cname(c.id)} : section du câble manquante`,
            en: `Circuit ${cname(c.id)}: cable section missing`,
            nl: `Kring ${cname(c.id)}: doorsnede van de kabel ontbreekt`,
          },
          target,
        });
    }
  }

  // The project's rules on the diagrams the survey would give.
  // (An inconsistent survey — concurrent edits — only skips the diagram checks.)
  const preview = surveyPreview(project, labels);
  if (preview) {
    const seen = new Set<string>();
    for (const f of checkProject(preview)) {
      if (COVERED.has(f.ruleId)) continue;
      const key = `${f.ruleId}|${f.message.en}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ id: f.ruleId, severity: f.severity, message: f.message, fromDiagram: true });
    }
  }
  const rank: Record<Severity, number> = { error: 0, warning: 1, info: 2 };
  return out.sort((a, b) => rank[a.severity] - rank[b.severity]);
}
